import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../client'
import { isLoggedIn } from '../client'
import type {
  Participant,
  Earnings,
  ActivityEntry,
  EnrolmentRequirements,
  SavedHandoffPoint,
} from '../types'

export function useMyProfile() {
  return useQuery({
    queryKey: ['my-profile'],
    queryFn: () => apiFetch<Participant>('api/v1/participants/me'),
  })
}

/**
 * What this vertical requires of a seller, straight from its registered policy.
 *
 * Fetched rather than hardcoded so the client never carries per-vertical knowledge:
 * Thrifts returns no fields at all, Stays returns licence evidence, and a vertical
 * with no policy 404s rather than offering an enrolment nobody can grant.
 */
export function useEnrolmentRequirements(vertical: string, enabled = true) {
  return useQuery({
    queryKey: ['enrolment-requirements', vertical],
    queryFn: () => apiFetch<EnrolmentRequirements>(`api/v1/enrolment-requirements/${vertical}`),
    enabled: enabled && isLoggedIn(),
  })
}

export const MY_HANDOFF_POINTS_KEY = ['my-handoff-points']

/**
 * The caller's saved locations — their address book.
 *
 * Signed-out callers skip the request entirely: public pages (a thrift listing, a
 * homestay) mount an address picker before the visitor has signed in, and firing an
 * authenticated request there would 401 for nothing.
 */
export function useMyHandoffPoints(enabled = true) {
  return useQuery({
    queryKey: MY_HANDOFF_POINTS_KEY,
    queryFn: () => apiFetch<SavedHandoffPoint[]>('api/v1/participants/me/handoff-points'),
    enabled: enabled && isLoggedIn(),
  })
}

export function useEarnings(participantId: string) {
  return useQuery({
    queryKey: ['earnings', participantId],
    queryFn: () => apiFetch<Earnings>(`api/v1/participants/${participantId}/earnings`),
    enabled: !!participantId,
  })
}

export function useMyActivity(role?: string | null, vertical?: string | null) {
  return useQuery({
    queryKey: ['my-activity', role ?? null, vertical ?? null],
    queryFn: () =>
      apiFetch<ActivityEntry[]>('api/v1/participants/me/activity', {
        params: { role: role || undefined, vertical: vertical || undefined },
      }),
  })
}
